<template>
  <transition name="itbvm" appear>
    <div
      v-if="value && featureEnabled"
      class="itbvm__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="itbvm-title"
      @mousedown.self="handleClose"
    >
      <div class="itbvm__card">
        <div class="itbvm__header">
          <span id="itbvm-title" class="itbvm__title">Filter inspections</span>
          <button
            type="button"
            class="itbvm__close"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="itbvm__groups">
          <!-- Status: plain checkbox multi-select, all options selected by
               default (no filtering). It has no "No Filter" option (ER-11310). -->
          <div class="itbvm__group" role="group" aria-label="Status">
            <span class="itbvm__group-title">Status</span>
            <div class="itbvm__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="status.includes(option.value)"
                @input="toggleStatus(option.value, $event)"
              />
            </div>
          </div>

          <!-- Learning / Quiz / Inspection: single-select radio groups whose
               first option is "No Filter" (selected by default). -->
          <div class="itbvm__group itbvm__group--radio">
            <RadioInput
              label="Learning"
              :availableItems="learningItems"
              :checkedItem="learningLabel"
              @changeradio="onLearningChange"
            />
          </div>

          <div class="itbvm__group itbvm__group--radio">
            <RadioInput
              label="Quiz"
              :availableItems="outcomeItems"
              :checkedItem="quizLabel"
              @changeradio="onQuizChange"
            />
          </div>

          <div class="itbvm__group itbvm__group--radio">
            <RadioInput
              label="Inspection"
              :availableItems="outcomeItems"
              :checkedItem="inspectionLabel"
              @changeradio="onInspectionChange"
            />
          </div>
        </div>

        <div class="itbvm__divider" />

        <div class="itbvm__footer">
          <button type="button" class="itbvm__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from "vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { LD_FLAG_IDEA_MDP_592 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11310 — InspectionsTableByVersionFilterModal.
 *
 * Filter modal for the InspectionsTableByVersion (Checkpoint editor) table.
 * Narrow the employee inspection list by Status, Learning, Quiz and Inspection.
 *
 * Groups:
 *   - Status:     plain checkbox multi-select; all options selected by default
 *                 (no filtering). No "No Filter" option.
 *   - Learning:   radio group — No Filter / Has Seen Learning / Did not See Learning.
 *   - Quiz:       radio group — No Filter / Not attempted / Passed / Failed / Not required.
 *   - Inspection: same shape as Quiz.
 *
 * Apply payload (stable lowercase snake_case values):
 *   { status: string[], learning: string, quiz: string, inspection: string }
 *   - status:     selected status values (default: all five).
 *   - learning /  the selected outcome value, or "no_filter" when the group's
 *     quiz /      No Filter option is selected (the default for all three).
 *     inspection
 *
 * Events:
 *   - apply:  selected filter state (above) — Apply button clicked.
 *   - reset:  default filter state (above) — Reset to defaults clicked
 *             (modal stays open).
 *   - input:  Boolean v-model for the open state (false when the modal closes).
 *   - close:  fired when the modal is dismissed without applying.
 */

const NO_FILTER = "no_filter";

/** RadioInput emits `null` for this exact label, so it must not change. */
const NO_FILTER_LABEL = "No Filter";

const STATUS_OPTIONS = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "passed", label: "Passed" },
  { value: "failed", label: "Failed" },
  { value: "not_applicable", label: "Not Applicable" },
];

const LEARNING_OPTIONS = [
  { value: "has_seen_learning", label: "Has Seen Learning" },
  { value: "did_not_see_learning", label: "Did not See Learning" },
];

const OUTCOME_OPTIONS = [
  { value: "not_attempted", label: "Not attempted" },
  { value: "passed", label: "Passed" },
  { value: "failed", label: "Failed" },
  { value: "not_required", label: "Not required" },
];

const props = defineProps({
  /** v-model open state. */
  value: { type: Boolean, default: false },
  /**
   * Pre-selected filter state applied when the modal opens, in the apply-payload
   * shape (`{ status, learning, quiz, inspection }`). `null` uses the defaults
   * (status all selected; learning, quiz and inspection on No Filter).
   */
  initialFilters: { type: Object, default: null },
});

const emit = defineEmits(["input", "close", "apply", "reset"]);

const statusOptions = STATUS_OPTIONS;
const learningItems = radioItems(LEARNING_OPTIONS);
const outcomeItems = radioItems(OUTCOME_OPTIONS);

const status = ref([]);
const learning = ref(null);
const quiz = ref(null);
const inspection = ref(null);
const featureEnabled = ref(true);

const learningLabel = computed(() => labelFor(LEARNING_OPTIONS, learning.value));
const quizLabel = computed(() => labelFor(OUTCOME_OPTIONS, quiz.value));
const inspectionLabel = computed(() => labelFor(OUTCOME_OPTIONS, inspection.value));

watch(
  () => props.value,
  (open) => {
    if (open) openModal();
  },
  { immediate: true }
);

async function openModal() {
  await ensureFeatureEnabled();
  if (!featureEnabled.value) return;
  syncFromProps();
}

async function ensureFeatureEnabled() {
  const proxy = getCurrentInstance()?.proxy;
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_592, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
}

function radioItems(options) {
  return [{ label: NO_FILTER_LABEL }, ...options.map((option) => ({ label: option.label }))];
}

function labelFor(options, value) {
  return options.find((option) => option.value === value)?.label ?? NO_FILTER_LABEL;
}

function valueForLabel(options, label) {
  return options.find((option) => option.label === label)?.value ?? null;
}

function defaultStatus() {
  return STATUS_OPTIONS.map((option) => option.value);
}

function normalizeStatus(value) {
  if (!Array.isArray(value)) return null;
  const valid = new Set(STATUS_OPTIONS.map((option) => option.value));
  return value.filter((entry) => valid.has(entry));
}

/**
 * Accepts the single value the group now emits, and still tolerates the array
 * form earlier callers passed in — the first recognised entry wins.
 */
function normalizeGroup(value, options) {
  const valid = new Set(options.map((option) => option.value));
  if (Array.isArray(value)) return value.find((entry) => valid.has(entry)) ?? null;
  return valid.has(value) ? value : null;
}

function syncFromProps() {
  const initial = props.initialFilters || {};
  status.value = normalizeStatus(initial.status) ?? defaultStatus();
  learning.value = normalizeGroup(initial.learning, LEARNING_OPTIONS);
  quiz.value = normalizeGroup(initial.quiz, OUTCOME_OPTIONS);
  inspection.value = normalizeGroup(initial.inspection, OUTCOME_OPTIONS);
}

function toggleStatus(value, checked) {
  const set = new Set(status.value);
  if (checked) set.add(value);
  else set.delete(value);
  status.value = [...set];
}

function onLearningChange(payload) {
  learning.value = valueForLabel(LEARNING_OPTIONS, payload?.value);
}

function onQuizChange(payload) {
  quiz.value = valueForLabel(OUTCOME_OPTIONS, payload?.value);
}

function onInspectionChange(payload) {
  inspection.value = valueForLabel(OUTCOME_OPTIONS, payload?.value);
}

function buildPayload() {
  return {
    status: [...status.value],
    learning: learning.value ?? NO_FILTER,
    quiz: quiz.value ?? NO_FILTER,
    inspection: inspection.value ?? NO_FILTER,
  };
}

function handleReset() {
  status.value = defaultStatus();
  learning.value = null;
  quiz.value = null;
  inspection.value = null;
  emit("reset", buildPayload());
}

function handleApply() {
  emit("apply", buildPayload());
  emit("input", false);
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => document.addEventListener("keydown", handleKeydown));
onUnmounted(() => document.removeEventListener("keydown", handleKeydown));
</script>

<style scoped>
.itbvm-enter-active,
.itbvm-leave-active {
  transition: opacity 180ms ease;
}

.itbvm-enter-from,
.itbvm-leave-to {
  opacity: 0;
}

.itbvm-enter-active .itbvm__card,
.itbvm-leave-active .itbvm__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.itbvm-enter-from .itbvm__card,
.itbvm-leave-to .itbvm__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  /* !important — required to override the base transition declarations so users
     who prefer reduced motion are never forced through the enter/leave animation. */
  .itbvm-enter-active,
  .itbvm-leave-active,
  .itbvm-enter-active .itbvm__card,
  .itbvm-leave-active .itbvm__card {
    transition: none !important;
  }
}

.itbvm__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.itbvm__card {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1032px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.itbvm__header,
.itbvm__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.itbvm__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.itbvm__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: -4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.itbvm__close:hover {
  background: rgba(152, 162, 179, 0.12);
}

.itbvm__close:focus-visible,
.itbvm__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.itbvm__groups {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 32px;
  align-items: start;
  width: 100%;
}

.itbvm__group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

/* RadioInput hardcodes Vuetify's pl-2 on its title and its options, so the
   checkbox column is inset by the same 8px to keep the four columns aligned. */
.itbvm__group-title {
  padding-left: 8px;
  color: #667085;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
}

.itbvm__options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 8px;
}

.itbvm__options ::v-deep .filter-checkbox {
  min-height: 20px;
  height: 20px;
}

.itbvm__options ::v-deep .icon-check .v-btn {
  width: 20px !important;
  height: 20px !important;
}

.itbvm__group--radio ::v-deep > div {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* RadioInput's own `.filter-items` rule sets the group title with !important,
   so matching the Figma title style needs the same weight to win. */
.itbvm__group--radio ::v-deep .filter-items {
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
  background: transparent !important;
  color: #667085 !important;
  font-size: 13px;
  line-height: 20px;
}

.itbvm__group--radio ::v-deep .v-input--radio-group,
.itbvm__group--radio ::v-deep .v-input--selection-controls {
  margin: 0;
  padding: 0;
}

.itbvm__group--radio ::v-deep .v-input--selection-controls .v-input__slot {
  margin: 0;
}

.itbvm__group--radio ::v-deep .v-input--radio-group__input {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.itbvm__group--radio ::v-deep .v-radio {
  height: 20px !important;
  min-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
  align-items: center;
}

.itbvm__group--radio ::v-deep .v-input--selection-controls__input {
  width: 16px !important;
  height: 16px !important;
  margin: 0 8px 0 0 !important;
  align-self: center;
}

.itbvm__group--radio ::v-deep .v-input--selection-controls__ripple {
  display: none;
}

.itbvm__group--radio ::v-deep .v-icon {
  font-size: 16px !important;
  height: 16px !important;
  width: 16px !important;
  line-height: 16px !important;
}

.itbvm__group--radio ::v-deep .v-input--selection-controls .v-radio > .v-label {
  margin-left: 0 !important;
  line-height: 20px;
  height: 20px;
}

.itbvm__group--radio ::v-deep .filter-radio .mdi-radiobox-marked::before {
  content: url("../assets/checkedRadioIconRing.svg") !important;
}

.itbvm__divider {
  flex: 0 0 auto;
  width: 100%;
  height: 1px;
  background: #e5e7eb;
}

.itbvm__reset {
  padding: 4px;
  margin: -4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
}

.itbvm__reset:hover {
  background: rgba(152, 162, 179, 0.08);
  color: #344054;
}

@media (max-width: 960px) {
  .itbvm__groups {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .itbvm__groups {
    grid-template-columns: 1fr;
  }
}
</style>
